import { setAuthed } from "@/ui/auth/authStore";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRouter, useSearch } from "@tanstack/react-router";
import { useState } from "react";

type SearchParams = {
  redirect?: string;
};

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
    credentials: "include",
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error((data && data.error) || `HTTP ${res.status}`);
  }

  return data as T;
}

export function AuthPage() {
  const router = useRouter();
  const search = useSearch({ from: "/auth" }) as SearchParams;

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleLogin(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await apiJson("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ phone, code }),
      });

      const redirectTo = search.redirect || "/rsvp";
      await router.navigate({ to: redirectTo });
    } catch (e: any) {
      setError(e?.message ?? "Login failed");
    } finally {
      setAuthed(true);
      setLoading(false);
    }
  }

  return (
    <Box sx={{ py: 4 }}>
      <Paper sx={{ p: 3, maxWidth: 500, mx: "auto" }}>
        <Stack component={"form"} spacing={2} onSubmit={handleLogin}>
          <Typography variant="h4">
            Fill your phone number or username and the 4 letter code that was
            sent with the wedding invitation
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            required
            label="Phone number or username"
            value={phone}
            placeholder="e.g. 040 123 1234"
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />

          <TextField
            required
            label="Invite code"
            value={code}
            placeholder="e.g. A4R2"
            onChange={(e) => setCode(e.target.value)}
            fullWidth
          />

          <Button
            variant="contained"
            disabled={!phone || !code || loading}
            type="submit"
          >
            continue
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
